import { useApiClient } from "@/infrastructure/rest";
import { ReleaseHighlightDto, UpsertReleaseHighlightDto } from "@/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RELEASE_HIGHLIGHT_BEHEER_QUERY_KEY, RELEASE_HIGHLIGHT_QUERY_KEY } from "./constants";
import { ReleaseHighlight } from "./domain";

const REFETCH_INTERVAL_MS = 3_600_000;

export function useReleaseHighlights() {
    const apiClient = useApiClient();

    return useQuery({
        queryKey: [RELEASE_HIGHLIGHT_QUERY_KEY],
        queryFn: async () => {
            const data = await apiClient.get<ReleaseHighlightDto[]>("/config/release-highlights");
            return ReleaseHighlight.fromDtos(data ?? []);
        },
        refetchInterval: REFETCH_INTERVAL_MS,
    });
}

export function useBeheerReleaseHighlights() {
    const apiClient = useApiClient();

    return useQuery({
        queryKey: [RELEASE_HIGHLIGHT_BEHEER_QUERY_KEY],
        queryFn: async () => {
            const data = await apiClient.get<ReleaseHighlightDto[]>("/beheer/release-highlights");
            return ReleaseHighlight.fromDtos(data ?? []);
        },
        refetchInterval: REFETCH_INTERVAL_MS,
    });
}

function useInvalidateReleaseHighlights() {
    const queryClient = useQueryClient();

    return async () => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: [RELEASE_HIGHLIGHT_BEHEER_QUERY_KEY] }),
            queryClient.invalidateQueries({ queryKey: [RELEASE_HIGHLIGHT_QUERY_KEY] }),
        ]);
    };
}

export function useCreateReleaseHighlight() {
    const apiClient = useApiClient();
    const invalidate = useInvalidateReleaseHighlights();

    return useMutation({
        mutationFn: async (bericht: UpsertReleaseHighlightDto) => {
            await apiClient.post("/beheer/release-highlights", bericht);
        },
        onSuccess: invalidate,
        onError: (error) => {
            console.error("Fout bij het aanmaken van release highlight", error);
        },
    });
}

type UpdateReleaseHighlight = UpsertReleaseHighlightDto & {
    id: number;
};

export function useUpdateReleaseHighlight() {
    const apiClient = useApiClient();
    const invalidate = useInvalidateReleaseHighlights();

    return useMutation({
        mutationFn: async ({ id, ...bericht }: UpdateReleaseHighlight) => {
            await apiClient.patch(`/beheer/release-highlights/${id}`, bericht);
        },
        onSuccess: invalidate,
        onError: (error) => {
            console.error("Fout bij het wijzigen van release highlight", error);
        },
    });
}

type DeleteReleaseHighlight = {
    id: number;
};

export function useDeleteReleaseHighlight() {
    const apiClient = useApiClient();
    const invalidate = useInvalidateReleaseHighlights();

    return useMutation({
        mutationFn: async ({ id }: DeleteReleaseHighlight) => {
            await apiClient.delete(`/beheer/release-highlights/${id}`);
        },
        onSuccess: invalidate,
        onError: (error) => {
            console.error("Fout bij het verwijderen van release highlight", error);
        },
    });
}
